/**
 * What a member gets back after tapping a session rating: the thank-you and,
 * after a 3, the Google review button (session-rating-review, 2026-11-03;
 * Mario's boxes 81, 82 and 84, 1–2 Nov 2026).
 *
 * A tapped rating (`rate:<booking>:<1|2|3>`, `session-rating-button.ts`) is
 * recorded without the LLM and answered by ONE deterministic message on the
 * booking-keyed wire (`session_rating_button:<booking>` / `thank_you`), so a
 * re-tap replays it rather than sending a second. {@link
 * planSessionRatingFollowUp} decides that message:
 *
 *   - No review link saved (`session_feedback_request.google_review_url`
 *     absent or null): today's three thank-yous, unchanged. There is no other
 *     switch (box 84, Q4c): saving the link on `/config` turns this on, and
 *     clearing it turns it off.
 *   - A link saved: a 3 gets the review text and a "Review us on Google"
 *     button opening the link (`send_whatsapp_message`'s `cta`); a 2 or a 1
 *     gets a plain question about what went wrong, which the chat agent saves
 *     on the rating (`add_session_feedback_comment`).
 *   - No link, even after a 3, for a member who opted out of marketing (Q6a),
 *     is tagged do-not-contact or was erased, or for staff: a tap from a
 *     number on the studio's staff list, or a member tagged `admin` (Google's
 *     policy bars reviews from employees; Mario's box 6, 3 Nov 2026). They
 *     get today's 3 text. Whether the tapping number is linked to the member
 *     plays no part.
 *
 * WHY ONLY AFTER A 3. Mario chose it on 1 Nov 2026 (box 81, Q1b) after the
 * plan set out the risk: Google's Maps content policy forbids businesses to
 * "selectively solicit positive reviews from customers", and the CMA's
 * fake-reviews guidance (CMA208, §4.5) names inviting only satisfied customers
 * as cherry-picking. Sending the link after every rating would avoid both. The
 * gate is deliberate, not an oversight; it is one condition below.
 *
 * The rule for the link itself is here too ({@link googleReviewUrlProblem}):
 * https, a Google host, at most {@link GOOGLE_REVIEW_URL_MAX} characters. The
 * L2 validator refuses anything else, and `/config` shows the same reason
 * while the operator types.
 *
 * Dependency-free leaf: the SPA, the L2 validator and the saga runtime all
 * import it, so it stays free of Node and package imports.
 */

import type { SessionRating } from "./session-rating-button.js";

/** The review button's label: 19 characters, inside WhatsApp's 20 (box 84, Q8). */
export const SESSION_RATING_REVIEW_BUTTON_TEXT = "Review us on Google";

/** The longest review link the config accepts. */
export const GOOGLE_REVIEW_URL_MAX = 300;

/**
 * The hosts a review link may point at: the Business Profile's own "Ask for
 * reviews" link (`https://g.page/r/<code>/review`), the review box by Place ID
 * (`https://search.google.com/local/writereview?placeid=…`), Maps, and Maps'
 * short links.
 */
export const GOOGLE_REVIEW_URL_HOSTS: ReadonlyArray<string> = [
  "g.page",
  "search.google.com",
  "www.google.com",
  "maps.app.goo.gl",
];

/**
 * Why `value` cannot be a studio's review link, as one short sentence for the
 * operator, or `null` when it can.
 */
export function googleReviewUrlProblem(value: string): string | null {
  if (value.length === 0) return "is empty";
  if (value.length > GOOGLE_REVIEW_URL_MAX) {
    return `is longer than ${GOOGLE_REVIEW_URL_MAX} characters`;
  }
  if (/\s/.test(value)) return "contains a space";
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return "is not a web address";
  }
  if (url.protocol !== "https:") return "must start with https://";
  if (url.username !== "" || url.password !== "" || url.port !== "") {
    return "must be a plain Google link";
  }
  if (!GOOGLE_REVIEW_URL_HOSTS.includes(url.hostname)) {
    return `must be a Google link (${GOOGLE_REVIEW_URL_HOSTS.join(", ")})`;
  }
  return null;
}

/** The thank-you to send: its text, and the review button when one goes. */
export interface SessionRatingFollowUpPlan {
  readonly body: string;
  /** `send_whatsapp_message`'s `cta`; `null` sends plain text. */
  readonly cta: { readonly url: string; readonly button_text: string } | null;
}

/** The thank-yous since 2026-08-04, still sent while no link is saved. */
const THANK_YOU_WITHOUT_LINK: Readonly<Record<SessionRating, string>> = {
  3: "Thanks for the feedback — brilliant to hear! Feel free to reply with any comments.",
  2: "Thanks for the feedback! If there's anything we could do better, feel free to reply with a comment.",
  1: "Thanks for being honest — sorry it wasn't up to scratch. If you can, reply with what went wrong so we can put it right.",
};

/** Mario's texts once a link is saved (boxes 81 and 82). Only the 3 has a button. */
const THANK_YOU_WITH_LINK: Readonly<Record<SessionRating, string>> = {
  3: "Thank you, brilliant to hear! If you have a minute, a quick Google review would mean a lot to the team. It's just one tap below.",
  2: "Thanks for rating your session! What would have made it a 3? Just reply here with a few words and we'll pass it straight to the team.",
  1: "Thank you for being honest, and sorry it wasn't up to scratch. What went wrong? Just reply here with a few words and we'll pass it straight to the team so we can put it right.",
};

/**
 * The one message answering a recorded tap rating. `rating` is the RECORDED
 * rating (on a re-tap, the first tap's), `reviewUrl` the studio's saved link
 * or `null`, `marketingOptedOut` whether no marketing may reach the member
 * (opted out, tagged do-not-contact, or erased), and `staff` whether the tap
 * is staff's: the tapping number is on the studio's staff list, or the member
 * is tagged `admin`.
 */
export function planSessionRatingFollowUp(args: {
  readonly rating: SessionRating;
  readonly reviewUrl: string | null;
  readonly marketingOptedOut: boolean;
  readonly staff: boolean;
}): SessionRatingFollowUpPlan {
  if (args.reviewUrl === null) {
    return { body: THANK_YOU_WITHOUT_LINK[args.rating], cta: null };
  }
  if (args.rating !== 3) {
    return { body: THANK_YOU_WITH_LINK[args.rating], cta: null };
  }
  if (args.marketingOptedOut || args.staff) {
    return { body: THANK_YOU_WITHOUT_LINK[3], cta: null };
  }
  return {
    body: THANK_YOU_WITH_LINK[3],
    cta: { url: args.reviewUrl, button_text: SESSION_RATING_REVIEW_BUTTON_TEXT },
  };
}
